import React, { useEffect, useRef, useState } from 'react';

import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useDocuments from 'hooks/document/useDocuments';
import { Document, DocumentList } from 'types';

import SelectorButton from './SelectorButton';

export interface DocumentAutoCompleteValue extends AutocompleteItem {
  documentId: string;
  label: string;
}

interface DocumentAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: DocumentAutoCompleteValue;
  onChange: (item: DocumentAutoCompleteValue | null) => void;
  type: Document['type'];
  isTemplate: boolean;
}

const DocumentAutoComplete = ({
  value,
  onChange,
  type,
  isTemplate,
  ...autocompleteProps
}: DocumentAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState<string>(value.label);
  const [debouncedSearch] = useDebouncedValue<string>(search, 250);

  const { data, isSuccess, isLoading } = useDocuments({ search: debouncedSearch, type, isTemplate });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: DocumentAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.pages
          .map(page => page.data.results)
          .flat()
          .map((document: DocumentList) => ({
            ...document,
            value: document.documentId,
            label: document.description,
          })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      withinPortal={true}
      {...autocompleteProps}
    />
  );
};

export default DocumentAutoComplete;
